import { useState } from "react"
import { useInertiaCable } from "@inertia-cable/react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { SkillTable } from "@/components/domain/skill-table"
import { NewSkillModal } from "@/components/domain/new-skill-modal"
import { HelpHint } from "@/components/ui/help-hint"
import type { Skill, SkillCategory } from "@/types/skill"

type Counts = {
  total: number
  knowledge_source: number
  response_action: number
  routing_logic: number
  workflow: number
  integration: number
}

type AgentOption = { id: number; name: string; key: string }

type Props = {
  skills: Skill[]
  counts: Counts
  selectedCategory: string | null
  selectedScope: "company" | "global"
  agents: AgentOption[]
  skills_stream: string
}

const CATEGORIES: Array<{ key: SkillCategory | null; label: string; countKey: keyof Counts }> = [
  { key: null,               label: "All",          countKey: "total" },
  { key: "knowledge_source", label: "Knowledge",    countKey: "knowledge_source" },
  { key: "response_action",  label: "Response",     countKey: "response_action" },
  { key: "routing_logic",    label: "Routing",      countKey: "routing_logic" },
  { key: "workflow",         label: "Workflow",     countKey: "workflow" },
  { key: "integration",      label: "Integration",  countKey: "integration" },
]

export default function SkillsIndex({ skills, counts, selectedCategory, selectedScope, agents, skills_stream }: Props) {
  const [modalOpen, setModalOpen] = useState(false)

  // Live updates: Skill / AgentSkill churn pings the company stream and
  // reloads the list.
  useInertiaCable(skills_stream, {
    only: ["skills", "counts"],
    debounce: 250,
  })

  function visitWith(params: Record<string, string | undefined>) {
    const qs = new URLSearchParams()
    if (params.category) qs.set("category", params.category)
    if (params.scope) qs.set("scope", params.scope)
    const url = qs.toString() ? `/skills?${qs.toString()}` : "/skills"
    router.visit(url, { preserveState: true, preserveScroll: true })
  }

  function setCategory(key: SkillCategory | null) {
    visitWith({ category: key ?? undefined, scope: selectedScope === "global" ? "global" : undefined })
  }

  function setScope(scope: "company" | "global") {
    visitWith({ category: selectedCategory ?? undefined, scope: scope === "global" ? "global" : undefined })
  }

  const subtitle = selectedScope === "global"
    ? "Starter skills our team built. Import one to copy it into your company."
    : `${counts.total} skill${counts.total === 1 ? "" : "s"} in this company`

  return (
    <AppShell>
      <PageHeader
        eyebrow="Agents / Skills"
        title="Skills catalog"
        subtitle={subtitle}
        actions={
          <Button variant="red" onClick={() => setModalOpen(true)} disabled={agents.length === 0}>
            + New skill
          </Button>
        }
      />

      {selectedScope === "global" && (
        <div className="flex items-center gap-2 text-sm font-medium text-app-text">
          <span>Global library</span>
          <HelpHint
            heading="What does Import do?"
            body="It makes a copy of the starter skill in your company. The copy starts as a draft so you can change it for your agent. The original starter skill stays untouched."
          />
        </div>
      )}

      <section className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          <Button
            variant={selectedScope !== "global" ? "red" : "ghost"}
            size="sm"
            onClick={() => setScope("company")}
          >
            My company
          </Button>
          <Button
            variant={selectedScope === "global" ? "red" : "ghost"}
            size="sm"
            onClick={() => setScope("global")}
          >
            Global library
          </Button>
        </div>
        <span className="h-4 w-px bg-border" />
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(({ key, label, countKey }) => {
            const active = (key ?? null) === (selectedCategory ?? null)
            return (
              <Button
                key={label}
                variant={active ? "red" : "ghost"}
                size="sm"
                onClick={() => setCategory(key)}
              >
                {label} <span className="ml-1.5 text-app-muted">{counts[countKey]}</span>
              </Button>
            )
          })}
        </div>
      </section>

      <SkillTable skills={skills} mode={selectedScope === "global" ? "global" : "company"} />

      <NewSkillModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        agents={agents}
      />
    </AppShell>
  )
}
